import {Injectable} from '@angular/core';
import {GroupColumn, Organizzazione, SearchResult} from '../_models/_Index';
import {environment} from '../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {AccountOrganizzazione, ModalitaModificaLotto, ModalitaModificaQuantita} from '../_models/AccountOrganizzazione';
import {constants} from '../_helpers/constants';
import {BehaviorSubject, firstValueFrom, Observable, Subject} from 'rxjs';
import {DuplicateOrganizzazione} from '../_models/DuplicateOrganizzazione';
import {StatoViste} from '../_models/StatoViste';
import {EsitoAllineamentoViste} from '../_models/EsitoAllineamentoViste';
import {CampoUtenteDef} from '../_models/CampoUtenteDef';
import {buildCampiUtenteColumns, OV_LIST_COLUMNS} from '../_models/ordine-vendita-list-columns';
import {VoceGiacenza} from '../_models/VoceGiacenza';
import {buildGiacenzaColumns} from '../_models/spedizioni/lista-spedizioni.columns';
import {getListaSpedizioniColumnLabel} from '../_helpers/lista-spedizioni-grid.helper';

const cachePrefix = 'archiconnect-v2-';

const defaultVenditaColumns = 'RifOrdine,RagioneSocialeCliente,StatoOrdine,DataConsegna,CodiceArticolo';

// Feature 064 — il selettore colonne Ordini Clienti deriva TUTTE le colonne statiche dal
// registry (fonte unica di verità: lista ↔ export ↔ ordinamento). Il default attivo resta
// invariato (5 colonne legacy) per non cambiare il comportamento di partenza.
const baseVenditaShowColumns: GroupColumn[] = OV_LIST_COLUMNS.map((c, i) => ({
  order: i,
  columnName: c.key,
  viewName: c.label,
  active: false,
  sortAsc: true,
}));

const baseVenditaGroupColumns: GroupColumn[] = [
  {order: 1, columnName: 'RagioneSocialeCliente', viewName: 'Cliente', active: false, sortAsc: true},
  {order: 2, columnName: 'StatoOrdine', viewName: 'Stato Ordine', active: false, sortAsc: true},
  {order: 3, columnName: 'StatoConsegna', viewName: 'Stato Consegna', active: false, sortAsc: true},
  {order: 4, columnName: 'CodiceArticolo', viewName: 'Articolo', active: false, sortAsc: true},
  {order: 5, columnName: 'Commessa', viewName: 'Commessa', active: false, sortAsc: true},
  {order: 6, columnName: 'Zona', viewName: 'Zona', active: false, sortAsc: true},
  {order: 7, columnName: 'Rappresentante', viewName: 'Rappresentante', active: false, sortAsc: true},
  {order: 8, columnName: 'DataOrdine', viewName: 'Data Ordine (per giorno)', active: false, sortAsc: true},
  {order: 9, columnName: 'DataConsegna', viewName: 'Data Consegna (per giorno)', active: false, sortAsc: true},
];

// ── Lista Spedizioni (feature 048) ─────────────────────────────────────────
// Colonne visibili in assenza di una configurazione salvata dall'utente: 14 chiavi,
// CodArt sempre come anchor. L'ordine della CSV determina l'ordine in tabella.
// `RagSoc` sta subito dopo `Cliente` (feature 117): con la nuova vista Cliente porta il
// solo codice, quindi senza la ragione sociale accanto la lista di default mostrerebbe
// una colonna di numeri. E' questa struttura a governare la visibilita di default —
// spedizioniShowColumnsConfig ricalcola `active` da qui, non dal flag in
// baseListaSpedizioniShowColumns. Chi ha gia una configurazione salvata conserva la
// propria e deve aggiungere "Ragione Sociale" dal selettore colonne.
const defaultListaSpedizioniColumns =
  'CodArt,DesArt,Cliente,RagSoc,Stato,DataSpedizione,DataConsegnaRich,NumDocOriginale,UnitaMisura,QtaOrdUmDoc,QtOrdImpUmDoc,NumRegistraz,NumProgrRiga,CodArtCliFor';

// Feature 149: l'etichetta di una colonna della Lista Spedizioni ha un solo punto di verita',
// `LISTA_SPEDIZIONI_COLUMNS`. Selettore e raggruppamento la leggono da li' invece di ripeterla:
// gli array restano scritti qui solo per `order` e `active`, che non coincidono con il catalogo
// (gli `order` hanno buchi e decidono la posizione per chi non ha una configurazione salvata).
const etichettaSpedizione = (key: string): string => getListaSpedizioniColumnLabel(key);
// Nel raggruppamento le colonne data raggruppano per giorno, e il nome lo dice.
const etichettaSpedizioneGiorno = (key: string): string => `${etichettaSpedizione(key)} (per giorno)`;

const baseListaSpedizioniShowColumns: GroupColumn[] = [
  {order: 0, columnName: 'CodArt', viewName: etichettaSpedizione('CodArt'), active: true, sortAsc: true},
  {order: 1, columnName: 'DesArt', viewName: etichettaSpedizione('DesArt'), active: true, sortAsc: true},
  {order: 2, columnName: 'Cliente', viewName: etichettaSpedizione('Cliente'), active: true, sortAsc: true},
  {order: 3, columnName: 'RagSoc', viewName: etichettaSpedizione('RagSoc'), active: true, sortAsc: true},
  {order: 4, columnName: 'Stato', viewName: etichettaSpedizione('Stato'), active: true, sortAsc: true},
  {order: 5, columnName: 'DataSpedizione', viewName: etichettaSpedizione('DataSpedizione'), active: true, sortAsc: true},
  {order: 6, columnName: 'DataConsegnaRich', viewName: etichettaSpedizione('DataConsegnaRich'), active: true, sortAsc: true},
  {order: 7, columnName: 'NumDocOriginale', viewName: etichettaSpedizione('NumDocOriginale'), active: true, sortAsc: true},
  {order: 8, columnName: 'UnitaMisura', viewName: etichettaSpedizione('UnitaMisura'), active: true, sortAsc: true},
  {order: 9, columnName: 'QtaOrdUmDoc', viewName: etichettaSpedizione('QtaOrdUmDoc'), active: true, sortAsc: true},
  {order: 10, columnName: 'QtOrdImpUmDoc', viewName: etichettaSpedizione('QtOrdImpUmDoc'), active: true, sortAsc: true},
  {order: 11, columnName: 'NumRegistraz', viewName: etichettaSpedizione('NumRegistraz'), active: true, sortAsc: true},
  {order: 12, columnName: 'NumProgrRiga', viewName: etichettaSpedizione('NumProgrRiga'), active: true, sortAsc: true},
  {order: 13, columnName: 'CodArtCliFor', viewName: etichettaSpedizione('CodArtCliFor'), active: true, sortAsc: true},
  // Campi aggiuntivi della vista, off di default (attivabili dal selettore).
  {order: 14, columnName: 'DatiSpedNum', viewName: etichettaSpedizione('DatiSpedNum'), active: false, sortAsc: true},
  {order: 15, columnName: 'DatiSpedLocalita1', viewName: etichettaSpedizione('DatiSpedLocalita1'), active: false, sortAsc: true},
  {order: 16, columnName: 'ValoreCampo2', viewName: etichettaSpedizione('ValoreCampo2'), active: false, sortAsc: true},
  {order: 17, columnName: 'PrioritaOrdine', viewName: etichettaSpedizione('PrioritaOrdine'), active: false, sortAsc: true},
  {order: 18, columnName: 'DataConsegnaConf', viewName: etichettaSpedizione('DataConsegnaConf'), active: false, sortAsc: true},
  {order: 21, columnName: 'RigaSollecitata', viewName: etichettaSpedizione('RigaSollecitata'), active: false, sortAsc: true},
  {order: 22, columnName: 'SerieNumApp', viewName: etichettaSpedizione('SerieNumApp'), active: false, sortAsc: true},
  {order: 23, columnName: 'QtaConf', viewName: etichettaSpedizione('QtaConf'), active: false, sortAsc: true},
  {order: 24, columnName: 'PrelevatoImb', viewName: etichettaSpedizione('PrelevatoImb'), active: false, sortAsc: true},
  {order: 26, columnName: 'NotaInternaPers', viewName: etichettaSpedizione('NotaInternaPers'), active: false, sortAsc: true},
  {order: 27, columnName: 'NotaInternaOsSpedp', viewName: etichettaSpedizione('NotaInternaOsSpedp'), active: false, sortAsc: true},
  // Colonne della nuova interrogazione (feature 117), off di default. `RagSoc` fa eccezione
  // ed e' piu' in alto, accanto a Cliente: e' l'unica che entra visibile di default.
  {order: 28, columnName: 'VarianteArt', viewName: etichettaSpedizione('VarianteArt'), active: false, sortAsc: true},
  {order: 29, columnName: 'TipoAnagrafica', viewName: etichettaSpedizione('TipoAnagrafica'), active: false, sortAsc: true},
  {order: 30, columnName: 'IdDocOrd', viewName: etichettaSpedizione('IdDocOrd'), active: false, sortAsc: true},
  {order: 31, columnName: 'IdDoc', viewName: etichettaSpedizione('IdDoc'), active: false, sortAsc: true},
  {order: 32, columnName: 'DataDocumento', viewName: etichettaSpedizione('DataDocumento'), active: false, sortAsc: true},
  {order: 33, columnName: 'QtInSped', viewName: etichettaSpedizione('QtInSped'), active: false, sortAsc: true},
  {order: 34, columnName: 'QtCons', viewName: etichettaSpedizione('QtCons'), active: false, sortAsc: true},
  {order: 35, columnName: 'QtaResOrv', viewName: etichettaSpedizione('QtaResOrv'), active: false, sortAsc: true},
  // Feature 157: famiglia articolo, off di default.
  {order: 36, columnName: 'CodFamiglia', viewName: etichettaSpedizione('CodFamiglia'), active: false, sortAsc: true},
];

// Solo le colonne raggruppabili (FR group modal).
const baseListaSpedizioniGroupColumns: GroupColumn[] = [
  {order: 1, columnName: 'Cliente', viewName: etichettaSpedizione('Cliente'), active: false, sortAsc: true},
  {order: 2, columnName: 'Stato', viewName: etichettaSpedizione('Stato'), active: false, sortAsc: true},
  {order: 3, columnName: 'DataSpedizione', viewName: etichettaSpedizioneGiorno('DataSpedizione'), active: false, sortAsc: true},
  {order: 4, columnName: 'DataConsegnaRich', viewName: etichettaSpedizioneGiorno('DataConsegnaRich'), active: false, sortAsc: true},
  {order: 5, columnName: 'UnitaMisura', viewName: etichettaSpedizione('UnitaMisura'), active: false, sortAsc: true},
  {order: 6, columnName: 'NumDocOriginale', viewName: etichettaSpedizione('NumDocOriginale'), active: false, sortAsc: true},
  {order: 7, columnName: 'DatiSpedLocalita1', viewName: etichettaSpedizione('DatiSpedLocalita1'), active: false, sortAsc: true},
  {order: 8, columnName: 'ValoreCampo2', viewName: etichettaSpedizione('ValoreCampo2'), active: false, sortAsc: true},
  {order: 9, columnName: 'DataConsegnaConf', viewName: etichettaSpedizioneGiorno('DataConsegnaConf'), active: false, sortAsc: true},
  {order: 10, columnName: 'SerieNumApp', viewName: etichettaSpedizione('SerieNumApp'), active: false, sortAsc: true},
];

const baseOrdiniGroupColumns: GroupColumn[] = [
  {order: 1, columnName: 'RifOrd', viewName: 'Riferimento Ordine', active: false, sortAsc: true},
  {order: 1, columnName: 'NumFase', viewName: 'Numero Fase', active: false, sortAsc: true},
  {order: 1, columnName: 'RisorsaId', viewName: 'Risorsa', active: false, sortAsc: true},
  {order: 1, columnName: 'Reparto', viewName: 'Reparto', active: false, sortAsc: true},
  {order: 1, columnName: 'CodCliente', viewName: 'Cliente', active: false, sortAsc: true},
  {order: 1, columnName: 'FaseContoLavoro', viewName: 'C/Lavoro', active: false, sortAsc: true},
  {order: 1, columnName: 'CodTerzista', viewName: 'Terzista', active: false, sortAsc: true},
  {order: 1, columnName: 'CodiceArticolo', viewName: 'Articolo', active: true, sortAsc: true},
  {order: 1, columnName: 'VarianteArt', viewName: 'Variante', active: false, sortAsc: true},
  {order: 2, columnName: 'LavorazioneId', viewName: 'Lavorazione', active: true, sortAsc: true},
  {order: 1, columnName: 'Commessa', viewName: 'Commessa', active: false, sortAsc: true},
  {order: 1, columnName: 'NumRegistrazione', viewName: 'Numero Registrazione (Ordini)', active: false, sortAsc: true},
  {order: 1, columnName: 'RifOdP', viewName: 'Rif. OdP (Righe)', active: false, sortAsc: true},
  {order: 1, columnName: 'IdDocumento', viewName: 'ID documento', active: false, sortAsc: true},
  {order: 1, columnName: 'IdRiga', viewName: 'Id Riga', active: false, sortAsc: true},
  {order: 1, columnName: 'IdRigaLavorazione', viewName: 'Id Riga Lavorazione', active: false, sortAsc: true},
  {order: 1, columnName: 'UdM', viewName: 'Unità di misura', active: false, sortAsc: true},
  {order: 1, columnName: 'RifLottoProdotto', viewName: 'Lotto Prodotto', active: false, sortAsc: true},
  {order: 1, columnName: 'Stato', viewName: 'Stato', active: false, sortAsc: true}
];

@Injectable({providedIn: 'root'})
export class OrganizzazioniService {
  public currentOrganizzazione: Observable<Organizzazione>;
  public currentAccountSettings: Observable<AccountOrganizzazione>;
  public handleSave: Subject<number> = new Subject();
  public showMenu: Subject<boolean> = new Subject();
  public currentOrganizzazioneSubject = new BehaviorSubject<Organizzazione>(null);
  public currentAccountSettingsSubject = new BehaviorSubject<AccountOrganizzazione>(null);
  protected readonly constants = constants;

  constructor(private http: HttpClient) {
    this.currentOrganizzazioneSubject.next(JSON.parse(localStorage.getItem(cachePrefix + 'currentOrg')));
    this.currentOrganizzazione = this.currentOrganizzazioneSubject.asObservable();
    this.currentAccountSettingsSubject.next(JSON.parse(localStorage.getItem(cachePrefix + 'currentOrgSettings')));
    this.currentAccountSettings = this.currentAccountSettingsSubject.asObservable();
  }

  public get currentOrganizzazioneValue(): Organizzazione {
    return this.currentOrganizzazioneSubject.value;
  }

  public get currentAccountSettingsValue(): AccountOrganizzazione {
    return this.currentAccountSettingsSubject.value;
  }

  public get organizzazioneId(): number {
    if (this.currentOrganizzazioneValue === null) {
      return 0;
    }
    return this.currentOrganizzazioneValue.id;
  };

  public get organizzazioneCodice(): string {
    if (this.currentOrganizzazioneValue === null) {
      return '';
    }
    return this.currentOrganizzazioneValue.codice;
  }

  public get gruppoCodice(): string {
    if (this.currentOrganizzazioneValue === null) {
      return '';
    }
    return this.currentOrganizzazioneValue.codiceGruppo;
  }

  public get filtroOrdini(): number {
    if (!this.currentAccountSettingsValue) {
      return 0;
    }
    return this.currentAccountSettingsValue.tipoFiltroOrdini;
  }

  // Usata nell'HTML.
  public get confirmQtaBtn(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeQtaBtn;
  }

  // Usata nell'HTML.
  public get showQtaPlusMinusBtn(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeQtaPlusMinusBtn;
  }

  public get confirmQtaAvvio(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeQtaAvvio;
  }

  public get confirmQtaPausa(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeQtaPausa;
  }

  public get chiudiQtaFine(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeQtaFine;
  }

  /**
   * Chiusura delle righe/fasi precedenti al Termina fase (issue #3477).
   * Il fallback `?? false` è esplicito: la cache localStorage scritta prima del rilascio non ha il
   * campo, e senza fallback il valore sarebbe `undefined`.
   */
  public get chiudiFasiPrecedenti(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeChiudiFasiPrecedenti ?? false;
  }

  public get confirmFine(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeConfirmFine;
  }

  public get transferMaterial(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeTranfer;
  }

  public get confirmAvanza(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeConfirmAvanza;
  }

  public get confirmQtaTotale(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeQtaTotale;
  }

  public get boxRequired(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeBoxRequired;
  }

  // Scadenza sessione per operatore (issue #3301). Restituisce l'override della membership senza
  // risolverlo: `null`/`undefined` = "eredita il globale" e `0` = "disattivato" sono due stati
  // distinti che solo resolveInactivityTimeout() può collassare in un numero.
  public get inactivityTimeoutMinutes(): number | null {
    return this.currentAccountSettingsValue?.inactivityTimeoutMinutes ?? null;
  }

  public get skipCq(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeSkipCq;
  }

  public get activeCqRientroTerzista(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeCqRientroTerzista ?? false;
  }

  public get activeScarto(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeScarto;
  }

  public get checkMateriale(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeCheckMateriale;
  }

  public get activeAvpExt(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeAvpExt;
  }

  public get autoLottoCode(): string {
    if (!this.currentAccountSettingsValue) {
      return '';
    }
    return this.currentAccountSettingsValue.autoLottoCode;
  }

  /**
   * Modificabilità del campo Lotto (issue #3462). Vedi ModalitaModificaLotto.
   *
   * Il fallback a `Libero` copre due casi distinti: nessuna impostazione caricata, e impostazioni
   * lette da una cache di localStorage scritta prima di questa release, che il campo non ce l'ha.
   * Senza fallback il secondo caso darebbe `undefined`, che non è nessuna delle tre modalità.
   */
  public get tipoModificaLotto(): ModalitaModificaLotto {
    if (!this.currentAccountSettingsValue) {
      return ModalitaModificaLotto.Libero;
    }
    return this.currentAccountSettingsValue.tipoModificaLotto ?? ModalitaModificaLotto.Libero;
  }

  /**
   * Modificabilità del campo Quantità (feature 150). Vedi ModalitaModificaQuantita.
   * Stesso fallback a `Libero` di tipoModificaLotto, per gli stessi due casi.
   */
  public get tipoModificaQuantita(): ModalitaModificaQuantita {
    if (!this.currentAccountSettingsValue) {
      return ModalitaModificaQuantita.Libero;
    }
    return this.currentAccountSettingsValue.tipoModificaQuantita ?? ModalitaModificaQuantita.Libero;
  }


  public get risorsePersonal(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeFiltroRisorse;
  }

  public get lavorazioniPersonal(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeFiltroLavorazioni;
  }

  public get codiceOperatore(): string {
    if (!this.currentAccountSettingsValue) {
      return '';
    }
    return this.currentAccountSettingsValue.codiceOperatore;
  }

  public get avvioType(): number {
    if (!this.currentAccountSettingsValue) {
      return 0;
    }
    return this.currentAccountSettingsValue.tipoAvvioRisorse;
  }

  public get viewGroup(): boolean {
    if (!this.currentAccountSettingsValue) {
      return false;
    }
    return this.currentAccountSettingsValue.activeViewGroup;
  }

  public get groupColumns(): GroupColumn[] {
    const columns = this.groupColumnsArray;
    return baseOrdiniGroupColumns.map(baseColumn => {
      if (columns.length === 0) {
        return {...baseColumn};
      }
      const colIndex = columns.indexOf(baseColumn.columnName);
      return {...baseColumn, active: colIndex > -1, order: colIndex};
    });
  }

  public get groupColumnsArray(): string[] {
    if (!this.currentAccountSettingsValue ||
      !this.currentAccountSettingsValue.groupColumns) {
      return [];
    }
    return this.currentAccountSettingsValue.groupColumns.split(',')
      .map(column => column.trim())
      .filter(column => column.length > 0);
  }

  public get groupColumnsLabelArray(): string[] {
    return this.groupColumnsArray
      .map(col => baseOrdiniGroupColumns.find(c => c.columnName === col)?.viewName ?? col);
  }

  public get venditaShowColumns(): string[] {
    if (!this.currentAccountSettingsValue?.colonneOrdiniCliente) {
      return defaultVenditaColumns.split(',');
    }
    return this.currentAccountSettingsValue.colonneOrdiniCliente.split(',');
  }

  // Feature 064 — variante del selettore che include le colonne dinamiche Campi Utente
  // (`Cu_<codice>`) derivate dalle definizioni attive dell'org, preservando ordine/attivazione
  // salvati in `colonneOrdiniCliente`. I `Cu_` salvati ma non più attivi vengono ignorati

  public get venditaGroupColumns(): GroupColumn[] {
    if (!this.currentAccountSettingsValue?.groupColumnsOrdiniCliente) {
      return baseVenditaGroupColumns;
    }
    const tokensByName = this.parseOrderedColumnTokens(this.currentAccountSettingsValue.groupColumnsOrdiniCliente);
    return baseVenditaGroupColumns.map(c => ({
      ...c,
      active: tokensByName.has(c.columnName),
      order: tokensByName.get(c.columnName)?.order ?? 99,
      sortAsc: tokensByName.get(c.columnName)?.asc ?? true,
    }));
  }

  public get venditaGroupColumnsArray(): string[] {
    if (!this.currentAccountSettingsValue?.groupColumnsOrdiniCliente) {
      return [];
    }
    return this.currentAccountSettingsValue.groupColumnsOrdiniCliente.split(',');
  }

  // ── Lista Spedizioni (feature 048) — mirror dei getter vendita* ──────────
  public get spedizioniShowColumns(): string[] {
    if (!this.currentAccountSettingsValue?.colonneListaSpedizioni) {
      return defaultListaSpedizioniColumns.split(',');
    }
    return this.currentAccountSettingsValue.colonneListaSpedizioni.split(',');
  }

  public get spedizioniShowColumnsConfig(): GroupColumn[] {
    return this.buildSpedizioniShowColumnsConfig(baseListaSpedizioniShowColumns);
  }

  // Catalogo esteso con le colonne giacenza dell'organizzazione (feature 118, issue #3354).
  // Le nuove colonne entrano SPENTE (active: false): una configurazione salvata non cambia da se'
  // e chi non ha mai personalizzato non si trova colonne in piu' senza averle chieste.
  // Gemello di venditaShowColumnsConfigWith per i campi utente.
  public spedizioniShowColumnsConfigWith(voci: VoceGiacenza[]): GroupColumn[] {
    const giacenzaColumns: GroupColumn[] = buildGiacenzaColumns(voci).map((c, i) => ({
      order: baseListaSpedizioniShowColumns.length + i,
      columnName: c.key,
      viewName: c.label,
      active: false,
      sortAsc: true,
    }));
    return this.buildSpedizioniShowColumnsConfig([...baseListaSpedizioniShowColumns, ...giacenzaColumns]);
  }

  private buildSpedizioniShowColumnsConfig(catalogo: GroupColumn[]): GroupColumn[] {
    const savedCols = this.currentAccountSettingsValue?.colonneListaSpedizioni
      ? this.currentAccountSettingsValue.colonneListaSpedizioni.split(',')
      : defaultListaSpedizioniColumns.split(',');
    return catalogo.map(c => ({
      ...c,
      active: savedCols.includes(c.columnName),
      order: savedCols.indexOf(c.columnName) >= 0 ? savedCols.indexOf(c.columnName) : 100 + c.order,
    }));
  }

  public get spedizioniGroupColumns(): GroupColumn[] {
    if (!this.currentAccountSettingsValue?.groupColumnsListaSpedizioni) {
      return baseListaSpedizioniGroupColumns;
    }
    const tokensByName = this.parseOrderedColumnTokens(this.currentAccountSettingsValue.groupColumnsListaSpedizioni);
    return baseListaSpedizioniGroupColumns.map(c => ({
      ...c,
      active: tokensByName.has(c.columnName),
      order: tokensByName.get(c.columnName)?.order ?? 99,
      sortAsc: tokensByName.get(c.columnName)?.asc ?? true,
    }));
  }

  public get spedizioniGroupColumnsArray(): string[] {
    if (!this.currentAccountSettingsValue?.groupColumnsListaSpedizioni) {
      return [];
    }
    return this.currentAccountSettingsValue.groupColumnsListaSpedizioni.split(',');
  }

  // (non presenti tra le colonne base → esclusi dal config).
  public venditaShowColumnsConfigWith(campiUtenteDefs: CampoUtenteDef[]): GroupColumn[] {
    const cuColumns: GroupColumn[] = buildCampiUtenteColumns(campiUtenteDefs).map((c, i) => ({
      order: baseVenditaShowColumns.length + i,
      columnName: c.key,
      viewName: c.label,
      active: false,
      sortAsc: true,
    }));
    return this.buildVenditaShowColumnsConfig([...baseVenditaShowColumns, ...cuColumns]);
  }

  public setCurrentOrganizzazioneValue(organizzazione: Organizzazione) {
    this.currentOrganizzazioneSubject.next(organizzazione);
    localStorage.setItem(cachePrefix + 'currentOrg', JSON.stringify(organizzazione));
  }

  public setCurrentAccountSettingsValue(accountOrganizzazione: AccountOrganizzazione) {
    this.currentAccountSettingsSubject.next(accountOrganizzazione);
    localStorage.setItem(cachePrefix + 'currentOrgSettings', JSON.stringify(accountOrganizzazione));
  }

  public async setCurrentOrganizzazioneValueById(organizzazioneId: number) {
    const _organizzazione = await this.getOrganizzazioneAsync(organizzazioneId);
    this.setCurrentOrganizzazioneValue(_organizzazione);
    const _accountOrg = await this.getMyAccountSettingsAsync(organizzazioneId);
    this.setCurrentAccountSettingsValue(_accountOrg);
    return {organizzazione: _organizzazione, settings: _accountOrg};
  }

  public isStandalone(): boolean {
    if (this.currentOrganizzazioneValue == null) {
      return false;
    }
    return this.currentOrganizzazioneValue.tipoInstallazione === 3;
  }

  async getOrganizzazioniFilteredAsync(search: string, page: number, count: number, sortField: string,
                                       sortAsc: boolean, showInactiveOnly = false) {
    return firstValueFrom(
      this.http.post<SearchResult<AccountOrganizzazione>>(`${environment.localApiUrl}api/Organizzazione/OrganizzazioniFiltered`,
        {search, page, count, sortField, sortAsc, showInactiveOnly})
    );
  }

  async getAllOrganizzazioniFilteredAsync(search: string, page: number, count: number, sortField: string,
                                          sortAsc: boolean, showInactiveOnly = false) {
    return firstValueFrom(
      this.http.post<SearchResult<AccountOrganizzazione>>(`${environment.localApiUrl}api/Organizzazione/OrganizzazioniFilteredAll`,
        {search, page, count, sortField, sortAsc, showInactiveOnly})
    );
  }

  async getOrganizzazioneAsync(orgId: number) {
    return firstValueFrom(this.http.get<Organizzazione>(`${environment.localApiUrl}api/Organizzazione/Organizzazione?orgId=${orgId}`));
  }

  async getMyAccountSettingsAsync(orgId: number) {
    return firstValueFrom(this.http.get<AccountOrganizzazione>(`${environment.localApiUrl}api/Organizzazione/MyAccountSettings?orgId=${orgId}`));
  }

  async createOrganizzazioneAsync(organizzazione: Organizzazione) {
    return firstValueFrom(this.http.post<Organizzazione>(`${environment.localApiUrl}api/Organizzazione/CreateOrganizzazione`, organizzazione));
  }

  async updateOrganizzazioneAsync(organizzazione: Organizzazione) {
    return firstValueFrom(this.http.post<Organizzazione>(`${environment.localApiUrl}api/Organizzazione/UpdateOrganizzazione`, organizzazione));
  }

  async archiveOrganizzazioneAsync(orgId: number) {
    return firstValueFrom(this.http.post<void>(`${environment.localApiUrl}api/Organizzazione/ArchiveOrganizzazione/${orgId}`, {}));
  }

  async deleteOrganizzazioneAsync(orgId: number, confirmation: string) {
    return firstValueFrom(this.http.post<void>(`${environment.localApiUrl}api/Organizzazione/DeleteOrganizzazione/${orgId}`,
      {confirmation}));
  }

  async duplicateOrganizzazioneAsync(duplicateOrg: DuplicateOrganizzazione) {
    return firstValueFrom(this.http.post<DuplicateOrganizzazione>(`${environment.localApiUrl}api/Organizzazione/DuplicateOrganizzazione`, duplicateOrg));
  }

  // Feature 158: stato e allineamento delle viste sul DB del gestionale (solo ADMIN).
  async getStatoVisteAsync(orgId: number) {
    return firstValueFrom(this.http.get<StatoViste>(`${environment.localApiUrl}api/Organizzazione/StatoViste/${orgId}`));
  }

  async allineaVisteAsync(orgId: number) {
    return firstValueFrom(this.http.post<EsitoAllineamentoViste>(
      `${environment.localApiUrl}api/Organizzazione/AllineaViste/${orgId}`, {}));
  }

  async getOperatoriOfOrganizzazioneAsync(orgId: number, search: string, page: 0, count: 0) {
    return firstValueFrom(this.http.post<SearchResult<AccountOrganizzazione>>(`${environment.localApiUrl}api/Organizzazione/OperatoriOfOrganizzazione/${orgId}`,
      {search, page, count,}));
  }

  async getAccountOrganizzazioneAsync(orgId: number, accountId: number) {
    return firstValueFrom(this.http
      .get<AccountOrganizzazione>(`${environment.localApiUrl}api/Organizzazione/GetAccountOrganizzazione?orgId=${orgId}&accountId=${accountId}`));
  }

  async addAccountToOrganizzazioneAsync(orgId: number, accId: number, userName: string, orgName: string) {
    return firstValueFrom(this.http.post<AccountOrganizzazione>(`${environment.localApiUrl}api/Organizzazione/AddAccountToOrganizzazione`,
      {organizzazioneId: orgId, accountId: accId, userName, orgName}));
  }

  async removeAccountFromOrganizzazioneAsync(orgId: number, accId: number) {
    return firstValueFrom(this.http.get<any>(`${environment.localApiUrl}api/Organizzazione/RemoveAccountFromOrganizzazione?orgId=${orgId}&accountId=${accId}`));
  }

  async removeAccountFromAllOrganizzazioniAsync(accId: number) {
    return firstValueFrom(this.http.get<any>(`${environment.localApiUrl}api/Organizzazione/RemoveAccountFromAllOrganizzazioni?accountId=${accId}`));
  }

  async saveAccountOrganizzazioneAsync(accountOrganizzazione: AccountOrganizzazione) {
    return firstValueFrom(this.http.post<any>(`${environment.localApiUrl}api/Organizzazione/SaveAccountOrganizzazione`, accountOrganizzazione));
  }

  // Attivazione/disattivazione membership operatore-organizzazione (issue #3169). Gate MANAGER solo FE.
  async setAccountOrganizzazioneActiveAsync(organizzazioneId: number, accountId: number, isActive: boolean) {
    return firstValueFrom(this.http.post<boolean>(`${environment.localApiUrl}api/Organizzazione/SetAccountOrganizzazioneActive`, {
      organizzazioneId,
      accountId,
      isActive
    }));
  }

  // Unico percorso di scrittura dei campi utente della membership (feature 136 / issue #3528):
  // saveAccountOrganizzazioneAsync è un full-row update e il backend ne ignora sempre campiUtente.
  // ⛔ Dopo il salvataggio NON ripopolare la cache delle impostazioni operatore da questo percorso.
  async updateAccountOrganizzazioneCampiUtenteAsync(
    organizzazioneId: number, accountId: number, campiUtente: string | null): Promise<{ result: string }> {
    return firstValueFrom(this.http.post<{ result: string }>(
      `${environment.localApiUrl}api/Organizzazione/UpdateAccountOrganizzazioneCampiUtente`,
      {organizzazioneId, accountId, campiUtente}));
  }

  // Unico percorso di scrittura dei filtri salvati della Lista Spedizioni (feature 153): l'operatore
  // e' quello del token. ⛔ Come per i campi utente, NON ripopolare la cache delle impostazioni.
  async updateAccountOrganizzazioneFiltriListaSpedizioniAsync(
    organizzazioneId: number, filtriListaSpedizioni: string | null): Promise<{ result: string }> {
    return firstValueFrom(this.http.post<{ result: string }>(
      `${environment.localApiUrl}api/Organizzazione/UpdateAccountOrganizzazioneFiltriListaSpedizioni`,
      {organizzazioneId, filtriListaSpedizioni}));
  }

  public toLocalIsoString(date: Date) {
    const tzOffset = -date.getTimezoneOffset(); // in minuti
    const sign = tzOffset >= 0 ? '+' : '-';
    const pad = (n: number) => n.toString().padStart(2, '0');

    const offsetHours = pad(Math.floor(Math.abs(tzOffset) / 60));
    const offsetMinutes = pad(Math.abs(tzOffset) % 60);

    return date.getFullYear() + '-' +
      pad(date.getMonth() + 1) + '-' +
      pad(date.getDate()) + 'T' +
      pad(date.getHours()) + ':' +
      pad(date.getMinutes()) + ':' +
      pad(date.getSeconds()) +
      sign + offsetHours + ':' + offsetMinutes;
  }

  roundByFactor = (value: number) => Math.round(value * this.constants.FACTOR) / this.constants.FACTOR;

  padLeft(num: number, size: number) {
    let s = num + '';
    while (s.length < size) {
      s = '0' + s;
    }
    return s;
  }

  /**
   * Ogni token può portare il suffisso di direzione `<` (crescente) / `>` (decrescente); token senza
   * suffisso ⇒ crescente (retrocompatibilità feature 038). Condiviso da venditaGroupColumns e
   * spedizioniGroupColumns, che leggono la stessa sintassi da due colonne CSV diverse.
   */
  private parseOrderedColumnTokens(csv: string): Map<string, { order: number; asc: boolean }> {
    const tokensByName = new Map<string, { order: number; asc: boolean }>();
    csv.split(',').forEach((raw, index) => {
      const token = raw.trim();
      if (!token) {
        return;
      }
      const last = token[token.length - 1];
      const name = (last === '<' || last === '>') ? token.slice(0, -1) : token;
      tokensByName.set(name, {order: index, asc: last !== '>'});
    });
    return tokensByName;
  }

  private buildVenditaShowColumnsConfig(baseColumns: GroupColumn[]): GroupColumn[] {
    const savedCols = this.currentAccountSettingsValue?.colonneOrdiniCliente
      ? this.currentAccountSettingsValue.colonneOrdiniCliente.split(',')
      : defaultVenditaColumns.split(',');
    return baseColumns.map(c => ({
      ...c,
      active: savedCols.includes(c.columnName),
      order: savedCols.indexOf(c.columnName) >= 0 ? savedCols.indexOf(c.columnName) : 100 + c.order,
    }));
  }
}
